import { brandKeyForUser } from "../storeBrand";
import { storeNameForLocationId, storeRecordForLabel } from "../lib/storeScope";

/** Schedule builder opens on this warehouse id when the catalog includes it. */
export const BUILDER_DEFAULT_LOCATION_ID = 71;

export const BRAND_THEMES = {
  stiq: {
    name: "STIQ",
    emailDomain: "stiq.gr",
    logoSrc: "/logo192.png",
    faviconSrc: "/favicon.ico",
    favicon32Src: "/favicon-32.png",
    appleTouchSrc: "/apple-touch-icon.png",
    ogImageSrc: "/logo512.png",
    vars: {
      "--green": "#1D22D7",
      "--green-mid": "#1519B0",
      "--green-light": "#E8E9FC",
      "--bg": "#F0EDE8",
      "--surface2": "#F5F2EC",
      "--surface3": "#EBE7E0",
      "--border": "#E0DBD3",
      "--dark": "#0D0F2B",
      "--header-dark": "#0D0F2B",
      "--text-primary": "#0D0D0D",
      "--text-secondary": "#6B7280",
      "--text-muted": "#5B6370",
    },
  },
  pg: {
    name: "Protein Garden",
    emailDomain: "proteingarden.gr",
    logoSrc: "/pg-logo192.png",
    faviconSrc: "/pg-favicon.ico",
    favicon32Src: "/pg-favicon-32.png",
    appleTouchSrc: "/pg-apple-touch-icon.png",
    ogImageSrc: "/pg-logo512.png",
    vars: {
      "--green": "#1B4332",
      "--green-mid": "#2D6A4F",
      "--green-light": "#D8EFE3",
      "--bg": "#F2F2ED",
      "--surface2": "#F7F7F2",
      "--surface3": "#EEEEE8",
      "--border": "#E2E2DA",
      "--dark": "#1A1A1A",
      "--header-dark": "#1B4332",
      "--text-primary": "#0D2B1D",
      "--text-secondary": "#4A6358",
      "--text-muted": "#8A9E94",
    },
  },
  dn: {
    name: "DiNAS",
    emailDomain: "dinas.gr",
    logoSrc: "/dn-logo192.png",
    faviconSrc: "/dn-favicon.ico",
    favicon32Src: "/dn-favicon-32.png",
    appleTouchSrc: "/dn-apple-touch-icon.png",
    ogImageSrc: "/dn-logo512.png",
    vars: {
      "--green": "#004ABE",
      "--green-mid": "#0039A0",
      "--green-light": "#E0ECFF",
      "--bg": "#F0F4FF",
      "--surface2": "#EBF0FB",
      "--surface3": "#E0E8F8",
      "--border": "#C8D8F0",
      "--dark": "#001A4D",
      "--header-dark": "#001A4D",
      "--text-primary": "#001A4D",
      "--text-secondary": "#3A5A8A",
      "--text-muted": "#5A78A8",
    },
  },
};

/** Semantic / shared tokens — same across all brands; always applied by applyThemeDom. */
export const SHARED_VARS = {
  "--surface": "#FFFFFF",
  "--lime": "#C2EF4A",
  "--lime-dark": "#A8D030",
  "--danger": "#E5533D",
  "--header-dark": "#0D0F2B",
};

export function brandShortLabel(user, storeRecords, brands) {
  const key = brandKeyForUser(user, storeRecords, brands);
  if (!key) return null;
  return key.toUpperCase();
}

export function resolveTheme(user, storeRecords, brands) {
  if (!user) return BRAND_THEMES.stiq;
  const key = brandKeyForUser(user, storeRecords, brands);
  if (key && BRAND_THEMES[key]) return BRAND_THEMES[key];
  return BRAND_THEMES.stiq;
}

export function applyThemeDom(theme) {
  const brandVars = theme.vars ?? BRAND_THEMES.stiq.vars;
  const vars = { ...SHARED_VARS, ...brandVars };
  Object.entries(vars).forEach(([k, v]) => {
    document.documentElement.style.setProperty(k, v);
  });

  document.head.querySelectorAll('link[rel="icon"]').forEach((link) => {
    if (link.getAttribute("sizes") === "32x32") {
      link.setAttribute("href", theme.favicon32Src);
    } else {
      link.setAttribute("href", theme.faviconSrc);
    }
  });
  const apple = document.head.querySelector('link[rel="apple-touch-icon"]');
  if (apple) apple.setAttribute("href", theme.appleTouchSrc);
}

/** True when a picker value is a real catalog store (not empty / All Stores). */
export function isSelectableStore(store) {
  const key = String(store || "").trim();
  return Boolean(key) && key !== "All Stores" && key.toLowerCase() !== "all";
}

/** First store from the API catalog. Empty until `/store-locations` loads. */
export function defaultPhysicalStoreForRole(storeNames, _role) {
  const names = storeNames || [];
  return names[0] || "";
}

/**
 * Builder default: catalog row for location 71 when present, otherwise the first row.
 * Empty until `/store-locations` loads. A testing catalog that omits 71 stays on the first row.
 */
export function defaultBuilderStore(storeNames, storeRecords) {
  const names = storeNames || [];
  const preferred = storeNameForLocationId(BUILDER_DEFAULT_LOCATION_ID, storeRecords);
  if (preferred && names.includes(preferred)) return preferred;
  return names[0] || "";
}

/** ShiftIQ ML / headcount / people-cost only exist for warehouse STORE rows. */
export function storeSupportsShiftIq(store, storeRecords) {
  if (!isSelectableStore(store)) return false;
  const rec = storeRecordForLabel(store, storeRecords);
  return String(rec?.location_type || "").toUpperCase() === "STORE";
}

/** Sidebar / settings store pin for chain-wide roles. */
export function userStorePinLabel(user) {
  if (!user) return "";
  if (user.role === "brand_manager") {
    const tag = brandShortLabel(user);
    return tag ? `All ${tag} Stores` : "All Brand Stores";
  }
  return user.store || "";
}

// Apply STIQ defaults synchronously so colour tokens exist before first paint
applyThemeDom(BRAND_THEMES.stiq);
